import { Box, BoxProps, Typography, styled, useTheme } from '@mui/material';
import React, { FC, PropsWithChildren, useMemo } from 'react';
import { motion } from 'framer-motion';

import { CARD_HEIGHT, CARD_WIDTH } from '../deck-card';
import { BhabiLogo } from '../../../../../assets';
import { blueGrey, grey } from '@mui/material/colors';

export const Thoka: FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // deterministic particles (no jitter on rerender)
  const particles = useMemo(
    () =>
      Array.from({ length: 10 }, (_, i) => ({
        i,
        x: ((i * 37) % 120) - 60,
        y: ((i * 53) % 120) - 60,
        s: 0.8 + (i % 4) * 0.15,
        d: 0.12 * (i % 6),
      })),
    []
  );

  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 3500,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        overflow: 'hidden',
        pointerEvents: 'none',

        // ✅ lighter, transparent overlay (screen still visible)
        background: isDark ? 'rgba(0,0,0,0.35)' : 'rgba(255,255,255,0.35)',
      }}
    >
      {/* ✨ burst particles (no wheel-like visuals) */}
      {particles.map(p => (
        <motion.div
          key={p.i}
          aria-hidden
          style={{
            position: 'absolute',
            width: 10,
            height: 10,
            borderRadius: 999,
            background: isDark
              ? 'radial-gradient(circle at 35% 35%, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.0) 70%)'
              : 'radial-gradient(circle at 35% 35%, rgba(118,75,162,0.75) 0%, rgba(118,75,162,0.0) 70%)',
            boxShadow: isDark
              ? '0 0 14px rgba(255,255,255,0.18)'
              : '0 0 14px rgba(118,75,162,0.18)',
            opacity: 0,
          }}
          animate={{
            x: [0, p.x, 0],
            y: [0, p.y, 0],
            scale: [0.6, p.s, 0.6],
            opacity: [0, 0.9, 0],
          }}
          transition={{
            duration: 2,
            ease: 'easeInOut',
            times: [0, 0.5, 1],
            repeat: Infinity,
            repeatDelay: 1,
            delay: p.d,
          }}
        />
      ))}

      {/* 💥 Main Thoka text */}
      <motion.div
        initial={{ scale: 0.5, opacity: 0.5 }}
        animate={{
          scale: [1, 2, 1],
          opacity: [1, 0.5, 1],
        }}
        transition={{
          duration: 2,
          ease: 'easeInOut',
          times: [0, 0.5, 1],
          repeat: Infinity,
          repeatDelay: 1,
        }}
        style={{
          position: 'relative',
          fontSize: '3rem',
          fontWeight: 900,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: isDark ? '#FFFFFF' : '#111827',
          textShadow: isDark
            ? '0 10px 28px rgba(0,0,0,0.55), 0 0 18px rgba(102,126,234,0.25)'
            : '0 10px 26px rgba(15,23,42,0.18), 0 0 18px rgba(118,75,162,0.12)',
          filter: 'drop-shadow(0 8px 18px rgba(0,0,0,0.25))',
        }}
      >
        {/* glow stroke */}
        <span
          style={{
            position: 'absolute',
            inset: 0,
            WebkitTextStroke: isDark
              ? '2px rgba(255,215,0,0.65)'
              : '2px rgba(102,126,234,0.45)',
            color: 'transparent',
            transform: 'translate(2px, 2px)',
          }}
        >
          Thoka
        </span>

        {/* glitch layer left */}
        <motion.span
          aria-hidden
          style={{
            position: 'absolute',
            inset: 0,
            color: isDark ? 'rgba(102,126,234,0.75)' : 'rgba(118,75,162,0.55)',
            transform: 'translate(-2px, 0)',
            mixBlendMode: isDark ? 'screen' : 'multiply',
          }}
          animate={{ opacity: [0.25, 0.65, 0.25], x: [-2, 2, -2] }}
          transition={{
            duration: 2,
            ease: 'easeInOut',
            times: [0, 0.5, 1],
            repeat: Infinity,
            repeatDelay: 1,
          }}
        >
          Thoka
        </motion.span>

        {/* glitch layer right */}
        <motion.span
          aria-hidden
          style={{
            position: 'absolute',
            inset: 0,
            color: isDark ? 'rgba(255,215,0,0.65)' : 'rgba(255,215,0,0.45)',
            transform: 'translate(2px, 0)',
            mixBlendMode: isDark ? 'screen' : 'multiply',
          }}
          animate={{ opacity: [0.2, 0.55, 0.2], x: [2, -2, 2] }}
          transition={{
            duration: 2,
            ease: 'easeInOut',
            times: [0, 0.5, 1],
            repeat: Infinity,
            repeatDelay: 1,
          }}
        >
          Thoka
        </motion.span>

        {/* main readable text */}
        <span style={{ position: 'relative', zIndex: 2 }}>Thoka</span>
      </motion.div>
    </Box>
  );
};

export const LogoCard: FC<PropsWithChildren<BoxProps>> = ({
  children,
  ...props
}) => {
  return (
    <Box
      {...{
        height: CARD_HEIGHT,
        width: CARD_WIDTH,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        bgcolor: theme =>
          theme.palette.mode === 'dark' ? blueGrey[900] : grey[500],
        boxShadow: theme => theme.shadows[6],
        sx: {
          borderRadius: '8px',
        },
        ...props,
      }}
    >
      <BhabiLogo
        style={{
          width: 50,
          height: 50,
        }}
      />
      {children}
    </Box>
  );
};

export interface ConcludedCardsDeckProps {
  concludedCards: number;
}

export const ConcludedCardsDeck: FC<ConcludedCardsDeckProps> = ({
  concludedCards,
}) => {
  return (
    <LogoCard>
      <Typography
        fontSize={{
          lg: '2rem',
          sm: '1.5rem',
        }}
        fontWeight={700}
        sx={{ color: theme => theme.palette.success.dark }}
      >
        {concludedCards}
      </Typography>
    </LogoCard>
  );
};

interface PlayerAvatarWrapperProps {
  isHorizontal?: boolean;
}
export const PlayersAvatarWrapper = styled(Box, {
  shouldForwardProp: props => props !== 'isHorizontal',
})<PlayerAvatarWrapperProps>(({ isHorizontal, theme }) => ({
  ...(isHorizontal
    ? {
        display: 'flex',
        width: '100%',
        flexWrap: 'wrap',
        justifyContent: 'space-evenly',
        alignItems: 'center',
        gap: theme.spacing(1),
        overflow: 'hidden',
        paddingTop: theme.spacing(2),
      }
    : {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        gap: theme.spacing(3),
      }),
}));

export const PlaygroundWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  height: `calc(100vh - ${CARD_HEIGHT})`,
  padding: theme.spacing(0.5, 2),
  paddingTop: theme.spacing(2),
}));

export interface InPlayCardsWrapperProps {
  gridWidth: number;
  numberOfPlayers: number;
}

export const InPlayCardsWrapper = styled(Box, {
  shouldForwardProp: props =>
    props !== 'gridWidth' && props !== 'numberOfPlayers',
})<InPlayCardsWrapperProps>(({ theme, gridWidth, numberOfPlayers }) => ({
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
  width: '100%',
  '& .inner_box': {
    position: 'relative',
    width: '80%',
    margin: '0 auto',
    display: 'grid',
    gridTemplateColumns: `repeat(${numberOfPlayers}, ${gridWidth}px)`,
    gap: theme.spacing(0.5),
  },
}));

export const BottomLeftBox = styled(Box)<{ isVerticalMode: boolean }>(
  ({ theme, isVerticalMode }) => ({
    position: 'absolute',
    bottom: `calc(${isVerticalMode ? 120 : 20}px + env(safe-area-inset-bottom))`,
    left: theme.spacing(2),
    zIndex: 2300,
  })
);
